import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Skeleton } from "@/components/ui/skeleton";
import { workspaceTreeQueryOptions } from "@/hooks/use-user-data-viewer";
import { buildTree, type TreeNode } from "./build-tree";
import { FileTree } from "./file-tree";
import { DocumentViewer } from "./document-viewer";

interface WorkspaceExplorerProps {
  workspaceId: string;
  workspaceName: string | null;
}

export function WorkspaceExplorer({
  workspaceId,
  workspaceName,
}: WorkspaceExplorerProps) {
  const [selectedFile, setSelectedFile] = useState<TreeNode | null>(null);
  const query = useQuery(workspaceTreeQueryOptions(workspaceId));

  const tree = query.data ? buildTree(query.data) : [];

  return (
    <div className="flex h-[calc(100dvh-12rem)] min-h-[24rem] gap-4">
      <div className="flex w-72 shrink-0 flex-col overflow-hidden rounded-xl border bg-card shadow-sm">
        <div className="border-b px-4 py-3">
          <p className="truncate text-sm font-semibold">
            {workspaceName || "워크스페이스"}
          </p>
          <p className="text-xs text-muted-foreground">폴더 · 문서</p>
        </div>
        <div className="flex-1 overflow-y-auto p-2">
          {query.isLoading && (
            <div className="space-y-2 p-2">
              <Skeleton className="h-6 w-full" />
              <Skeleton className="h-6 w-5/6" />
              <Skeleton className="h-6 w-4/6" />
            </div>
          )}
          {query.isError && (
            <p className="p-2 text-sm text-destructive">
              트리를 불러오지 못했습니다.
            </p>
          )}
          {query.data && tree.length === 0 && (
            <p className="p-2 text-sm text-muted-foreground">
              폴더/문서가 없습니다.
            </p>
          )}
          {tree.length > 0 && (
            <FileTree
              nodes={tree}
              selectedNodeId={selectedFile?.id ?? null}
              onSelectFile={setSelectedFile}
            />
          )}
        </div>
      </div>

      <div className="flex min-w-0 flex-1 flex-col overflow-hidden rounded-xl border bg-card shadow-sm">
        <DocumentViewer
          fileId={selectedFile?.fileId ?? null}
          fallbackTitle={selectedFile?.name ?? null}
        />
      </div>
    </div>
  );
}
